import { useState } from "react";
import { AlertCircle, FileText, Shield } from "lucide-react";
import { toast } from "sonner";

/**
 * UpdatedTermsModal
 *
 * Blocking modal that forces users to accept updated legal terms
 * before they can continue using the platform
 */
export default function UpdatedTermsModal({ documents, onAccept, onDecline }) {
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!accepted) return;

    setIsSubmitting(true);
    try {
      await onAccept();
    } catch (error) {
      console.error("Error accepting terms:", error);
      toast.error("Failed to accept terms. Please try again.");
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    if (
      confirm("Declining the updated terms will log you out. Are you sure?")
    ) {
      onDecline();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-[#10B981] text-white p-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Updated Legal Terms</h2>
              <p className="text-white/90 mt-1">
                We've updated our legal documents. Please review and accept to
                continue.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-gray-600 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Action Required</p>
                <p>
                  You must review and accept the updated terms to continue using
                  Vouchee™. If you decline, you will be logged out.
                </p>
              </div>
            </div>
          </div>

          {/* Documents List */}
          <div className="space-y-4">
            {documents.map((doc) => (
              <div
                key={doc.type}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start space-x-3">
                  <div className="bg-green-100 p-2 rounded">
                    {doc.type === "terms_of_service" ? (
                      <FileText className="w-5 h-5 text-green-600" />
                    ) : (
                      <Shield className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{doc.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Version {doc.version} • Effective{" "}
                      {new Date(doc.effectiveDate).toLocaleDateString()}
                    </p>

                    {/* Changes Summary */}
                    {doc.changes && doc.changes.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          What's New:
                        </p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {doc.changes.slice(0, 5).map((change, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="text-green-600 mr-2">•</span>
                              <span>{change}</span>
                            </li>
                          ))}
                          {doc.changes.length > 5 && (
                            <li className="text-gray-500 italic">
                              ...and {doc.changes.length - 5} more updates
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* View Document Link */}
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-3 text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      Read Full Document →
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Key Highlights */}
          <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">
              Key Changes Summary:
            </h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>
                  <strong>Multi-Partner Sharing:</strong> Client information may
                  be shared with 5-10+ partners simultaneously
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>
                  <strong>AI Decision-Making:</strong> Artificial intelligence
                  determines partner recommendations and data distribution
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>
                  <strong>Permanent Sharing:</strong> Information cannot be
                  recalled or deleted once shared with partners
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>
                  <strong>No Partner Vetting:</strong> We do not verify partner
                  compliance with privacy laws or security practices
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>
                  <strong>Worldwide Transfers:</strong> Data may be transferred
                  to any country, including those with weak privacy laws
                </span>
              </li>
            </ul>
          </div>

          {/* Acceptance Checkbox */}
          <div className="mt-6 bg-white border-2 border-green-200 rounded-lg p-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">
                <strong className="text-gray-900">
                  I have read and agree to the updated Terms of Service and
                  Privacy Policy.
                </strong>{" "}
                I understand that:
                <ul className="mt-2 space-y-1 ml-4">
                  <li>
                    • Client information will be shared with multiple partners
                  </li>
                  <li>
                    • AI makes automated decisions about data distribution
                  </li>
                  <li>
                    • Information sharing is permanent and cannot be recalled
                  </li>
                  <li>• I am responsible for obtaining necessary consents</li>
                  <li>
                    • I accept all risks associated with information sharing
                  </li>
                </ul>
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <button
            onClick={handleDecline}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
          >
            Decline & Log Out
          </button>
          <button
            onClick={handleAccept}
            disabled={!accepted || isSubmitting}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
              accepted && !isSubmitting
                ? "bg-[#10B981] text-white hover:bg-[#059669]"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? "Accepting..." : "I Accept"}
          </button>
        </div>
      </div>
    </div>
  );
}
